/**
 * OperadorPin -- Identificacion de operador (colaborador) por PIN.
 * Modelo POS: el negocio esta logueado, los colaboradores se turnan con PIN.
 * Guarda identidad en sessionStorage (caduca al cerrar pestana).
 *
 * @module presentation/components/OperadorPin
 */

import { ApiBase } from '../../infrastructure/api/ApiBase.js';
import { getSelectedBusiness } from './BusinessSelector.js';
import { respuestaDelPin } from '../../application/asistencia/respuestaDelPin.js';

const KEYS = {
  ID: 'nx_operador_id', NOMBRE: 'nx_operador_nombre', CARGO: 'nx_operador_cargo', TS: 'nx_operador_ts',
  // KIOSK-DESPERDICIO-V1 (bit #3021): grupo (titular+reemplazo) del operador, para que
  // el kiosk solo le muestre insumos de SU grupo. Additivo -- consumidores viejos
  // (MarcacionView etc.) ignoran estas keys sin romperse.
  GRUPO: 'nx_operador_grupo', GRUPO_REEMPLAZO: 'nx_operador_grupo_reemplazo'
};
const TIMEOUT_MS = 30 * 60 * 1000; // 30 min inactivity

export function getOperador() {
  const id = sessionStorage.getItem(KEYS.ID);
  if (!id) return null;
  const ts = parseInt(sessionStorage.getItem(KEYS.TS) || '0');
  if (Date.now() - ts > TIMEOUT_MS) {
    clearOperador();
    return null;
  }
  return {
    id,
    nombre: sessionStorage.getItem(KEYS.NOMBRE) || '',
    cargo: sessionStorage.getItem(KEYS.CARGO) || '',
    grupo: sessionStorage.getItem(KEYS.GRUPO) || '',
    grupoReemplazo: sessionStorage.getItem(KEYS.GRUPO_REEMPLAZO) || ''
  };
}

function setOperador(id, nombre, cargo, grupo, grupoReemplazo) {
  sessionStorage.setItem(KEYS.ID, id);
  sessionStorage.setItem(KEYS.NOMBRE, nombre);
  sessionStorage.setItem(KEYS.CARGO, cargo);
  sessionStorage.setItem(KEYS.GRUPO, grupo || '');
  sessionStorage.setItem(KEYS.GRUPO_REEMPLAZO, grupoReemplazo || '');
  sessionStorage.setItem(KEYS.TS, String(Date.now()));
}

function clearOperador() {
  Object.values(KEYS).forEach(k => sessionStorage.removeItem(k));
}

export function refreshOperadorTimeout() {
  if (sessionStorage.getItem(KEYS.ID)) {
    sessionStorage.setItem(KEYS.TS, String(Date.now()));
  }
}

export async function setupOperadorPin() {
  const $wrap = document.getElementById('nx-operador-wrap');
  if (!$wrap) return;

  render($wrap);

  // Refresh timeout on user activity
  ['click', 'keydown', 'touchstart'].forEach(evt =>
    document.addEventListener(evt, refreshOperadorTimeout, { passive: true })
  );
}

function render($wrap) {
  const op = getOperador();
  if (op) {
    $wrap.innerHTML = `
      <div id="nx-op-badge" style="display:flex;align-items:center;gap:6px;cursor:pointer;padding:2px 8px;background:#0d2818;border:1px solid #238636;border-radius:4px" title="Click para cambiar operador">
        <span style="font-size:0.875rem;color:#3fb950;font-weight:600">${esc(op.nombre)}</span>
        <button id="nx-op-logout" style="background:none;border:none;color:#6e7681;font-size:0.875rem;cursor:pointer;padding:0" title="Salir">&times;</button>
      </div>`;
    document.getElementById('nx-op-logout')?.addEventListener('click', (e) => {
      e.stopPropagation();
      clearOperador();
      render($wrap);
      window.dispatchEvent(new CustomEvent('operador:changed'));
    });
    document.getElementById('nx-op-badge')?.addEventListener('click', () => showPinModal($wrap));
  } else {
    $wrap.innerHTML = `
      <button id="nx-op-identify" class="btn-ghost" style="font-size:0.875rem;padding:3px 8px;border:1px dashed #30363d;color:#8b949e">Identificarse</button>`;
    document.getElementById('nx-op-identify')?.addEventListener('click', () => showPinModal($wrap));
  }
}

/**
 * COLAB-PANEL-P1 (bit #3293): modal custom de info/error -- reemplaza alert()
 * nativo (regla de hierro del SKILL, ningun prompt()/alert()/confirm()).
 */
function showInfoModal(message) {
  const old = document.getElementById('nx-op-info-modal');
  if (old) old.remove();
  const modal = document.createElement('div');
  modal.id = 'nx-op-info-modal';
  modal.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,0.7);z-index:10000;display:flex;align-items:center;justify-content:center;padding:20px';
  modal.innerHTML = `
    <div style="background:#161b22;border:1px solid #30363d;border-radius:12px;padding:20px;width:100%;max-width:340px">
      <p style="margin:0 0 16px;font-size:0.9rem;color:#e1e4e8;line-height:1.5">${esc(message)}</p>
      <button id="nx-op-info-ok" type="button" style="width:100%;padding:10px;background:#238636;border:none;border-radius:6px;color:#fff;font-weight:600;cursor:pointer">Entendido</button>
    </div>`;
  document.body.appendChild(modal);
  const close = () => modal.remove();
  document.getElementById('nx-op-info-ok').addEventListener('click', close);
  modal.addEventListener('click', (e) => { if (e.target === modal) close(); });
}

async function showPinModal($wrap) {
  const bizId = getSelectedBusiness();
  if (!bizId) return;

  // Load collaborators
  const res = await ApiBase.get(`/api/v1/businesses/${bizId}/colaboradores?page=1&pageSize=50&estado=activo`);
  if (!res.ok) {
    // Sin esto, un fallo de red/API se disfrazaba de "no hay colaboradores".
    // COLAB-PANEL-P1 (bit #3293): alert() nativo -> modal custom (regla de hierro).
    showInfoModal('No se pudo cargar la lista de colaboradores: ' + (res.error || 'intenta de nuevo'));
    return;
  }
  const items = res.data?.data?.items || res.data?.items || [];

  if (items.length === 0) {
    showInfoModal('No hay colaboradores activos para este negocio.');
    return;
  }

  // Create modal overlay
  const modal = document.createElement('div');
  modal.id = 'nx-op-modal';
  modal.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,0.7);z-index:9999;display:flex;align-items:center;justify-content:center';
  modal.innerHTML = `
    <div style="background:#161b22;border:1px solid #30363d;border-radius:12px;padding:24px;width:340px;max-height:80vh;overflow-y:auto">
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:16px">
        <h3 style="margin:0;font-size:1rem">Selecciona tu perfil</h3>
        <button id="nx-op-close" style="background:none;border:none;color:#8b949e;font-size:1.2rem;cursor:pointer">&times;</button>
      </div>
      <div id="nx-op-list" style="display:flex;flex-direction:column;gap:8px">
        ${items.map(c => `
          <div class="nx-op-item" data-id="${esc(c.id)}" data-name="${esc(c.nombreCompleto || c.nombre_completo || '')}" data-cargo="${esc(c.cargo || '')}" data-grupo="${esc(c.grupoConteoNombre || '')}" data-grupo-reemplazo="${esc(c.grupoConteoReemplazoNombre || '')}" style="padding:12px;background:#0d1117;border:1px solid #21262d;border-radius:8px;cursor:pointer;transition:border-color 0.15s">
            <div style="font-weight:600;font-size:0.875rem">${esc(c.nombreCompleto || c.nombre_completo || '')}</div>
            <div style="font-size:0.875rem;color:#8b949e">${esc(c.cargo || '')} ${c.tienePIN || c.tiene_pin ? '' : '<span style="color:#d29922">(sin PIN)</span>'}</div>
          </div>
        `).join('')}
      </div>
      <div id="nx-op-pin-section" style="display:none;margin-top:16px">
        <p id="nx-op-pin-label" style="font-size:0.875rem;color:#8b949e;margin-bottom:8px"></p>
        <input id="nx-op-pin-input" type="password" inputmode="numeric" enterkeyhint="done" maxlength="6" placeholder="PIN" style="width:100%;padding:12px;background:#0d1117;border:1px solid #30363d;border-radius:6px;color:#e1e4e8;font-size:1.2rem;text-align:center;letter-spacing:8px" autofocus>
        <button id="nx-op-pin-confirm" type="button" style="width:100%;margin-top:10px;padding:12px;background:#238636;border:none;border-radius:6px;color:#fff;font-size:0.95rem;font-weight:600;cursor:pointer">Confirmar</button>
        <p id="nx-op-pin-error" style="color:#f85149;font-size:0.875rem;margin-top:6px;display:none"></p>
      </div>
    </div>`;

  document.body.appendChild(modal);

  let selectedColab = null;

  // Close
  document.getElementById('nx-op-close')?.addEventListener('click', () => modal.remove());
  modal.addEventListener('click', (e) => { if (e.target === modal) modal.remove(); });

  // Select collaborator
  modal.querySelectorAll('.nx-op-item').forEach(item => {
    item.addEventListener('click', () => {
      selectedColab = { id: item.dataset.id, name: item.dataset.name, cargo: item.dataset.cargo, grupo: item.dataset.grupo, grupoReemplazo: item.dataset.grupoReemplazo };
      modal.querySelectorAll('.nx-op-item').forEach(i => i.style.borderColor = '#21262d');
      item.style.borderColor = '#58a6ff';
      const pinSection = document.getElementById('nx-op-pin-section');
      const pinLabel = document.getElementById('nx-op-pin-label');
      const pinInput = document.getElementById('nx-op-pin-input');
      pinSection.style.display = 'block';
      pinLabel.textContent = `PIN de ${selectedColab.name}:`;
      pinInput.value = '';
      pinInput.focus();
      document.getElementById('nx-op-pin-error').style.display = 'none';
    });
  });

  // KIOSK-FIX (bit #2977): en movil el teclado numerico (inputmode=numeric) no siempre
  // dispara keydown con key==='Enter', y no habia boton de confirmacion -> imposible
  // identificarse. Logica extraida a verifyPin(); la disparan Enter Y el boton Confirmar.
  let verifying = false; // evita doble submit (Enter + tap casi simultaneos)
  async function verifyPin() {
    if (verifying || !selectedColab) return;
    const pinInput = document.getElementById('nx-op-pin-input');
    const pin = pinInput.value.trim();
    if (!pin) return;

    const errorEl = document.getElementById('nx-op-pin-error');
    verifying = true;
    try {
      const res = await ApiBase.post(`/api/v1/businesses/${bizId}/asistencia/verify-pin`, {
        colaboradorId: selectedColab.id,
        pin
      }, { reintentar429: false });
      if (res.ok && res.data?.success) {
        setOperador(selectedColab.id, selectedColab.name, selectedColab.cargo, selectedColab.grupo, selectedColab.grupoReemplazo);
        modal.remove();
        render($wrap);
        window.dispatchEvent(new CustomEvent('operador:changed'));
      } else {
        // PANEL-PIN-UNIFICADO-T1: antes TODO fallo (frenado, red, 500) decia 'PIN incorrecto'.
        errorEl.textContent = respuestaDelPin(res, selectedColab.name).texto;
        errorEl.style.display = 'block';
        pinInput.value = '';
        pinInput.focus();
      }
    } catch {
      errorEl.textContent = respuestaDelPin({ ok: false, code: 'NETWORK' }, selectedColab.name).texto;
      errorEl.style.display = 'block';
      document.getElementById('nx-op-pin-input').value = '';
    } finally {
      verifying = false;
    }
  }

  document.getElementById('nx-op-pin-input')?.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') verifyPin();
  });
  document.getElementById('nx-op-pin-confirm')?.addEventListener('click', verifyPin);
}

// Deuda 4: era una copia del escape; ahora es el compartido.
import { esc } from '../shared/texto.js';
